import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { permissionDeniedTitle } from '@/lib/permissionUi';

const TRACE_EXTENSIONS = ['.txt', '.csv', '.tsv', '.log', '.json', '.xlsx'];

const STAGE_LABELS = {
  catalog_verified: 'Catalog verification',
  pfcg_refresh: 'PFCG refresh',
  authorization_merge: 'Authorization defaults',
  authorization_review: 'PFCG & SU24 authorization preparation',
  profile_verification: 'Initial profile',
  user_assignment: 'Intended user',
  user_comparison: 'Initial user comparison',
  trace_review: 'Optional STAUTHTRACE analysis',
  cleanup_review: 'Authorization review',
  authorization_maintenance: 'Authorization cleanup',
  profile_regeneration: 'Profile regeneration',
  final_user_comparison: 'Final user comparison',
  transport_verification: 'Transport verification',
  complete: 'Complete',
};

const NEXT_ACTION = {
  pfcg_refresh: 'Next: execute the PFCG refresh for this role.',
  authorization_merge: 'Next: merge the authorization defaults. Profile generation stays separate.',
  authorization_review: 'Next: maintain and approve required organizational values. Optional blank fields may remain blank.',
  profile_verification: 'HALO is generating and verifying the initial profile automatically.',
  user_assignment: 'Next: check the intended user. If the role and generated profile are already effective, request that user\'s STAUTHTRACE. Offer role assignment only when the role is missing.',
  user_comparison: 'Next: run SAP user comparison so the generated profile becomes effective, then request this user\'s STAUTHTRACE.',
  trace_review: 'Next: review the authorization values. Uploading STAUTHTRACE is optional.',
  cleanup_review: 'Next: choose any newly introduced values to remove, or continue without cleanup. STAUTHTRACE is optional.',
  authorization_maintenance: 'Next: retry the approved cleanup. The same approval is not written twice.',
  profile_regeneration: 'Next: approve and regenerate the profile after cleanup. This approval is separate from the initial profile.',
  final_user_comparison: 'Next: run final user comparison. This is separate from profile generation.',
  transport_verification: 'Next: verify the shared customizing transport.',
  complete: 'Technical follow-up is complete. Ready for QA / functional validation. No transport was released.',
};

const OBJECT_TEXT = {
  F_CLM_BAM: 'Bank Account Management',
  F_BNKA_MAN: 'Bank Account Management',
  F_BNKA_MAO: 'Bank Account Master',
  F_BNKA_BUK: 'Bank Account Company Code',
  F_CLM_RULE: 'Cash Management Rules',
  F_CLM_SIG: 'Cash Management Signatories',
  F_BKPF_GSB: 'Accounting Document Business Area',
  K_PCA_MD: 'Profit Center Master Data',
};

const FIELD_TEXT = {
  ACTVT: 'Activity',
  BUKRS: 'Company Code',
  GSBER: 'Business Area',
  KOKRS: 'Controlling Area',
  PRCTR: 'Profit Center',
  FCLM_BUKRS: 'Cash Mgmt Company Code',
  FCLM_GSBER: 'Cash Mgmt Business Area',
  FCLM_KOKRS: 'Cash Mgmt Controlling Area',
  FCLM_PRCTR: 'Cash Mgmt Profit Center',
  FCLM_ACTY: 'Cash Management Activity',
  FCLM_SGMT: 'Cash Management Segment',
  BEGRU: 'Authorization Group',
  RLTYP: 'Business Partner Role',
  BBANKS: 'Bank Key',
  SIGTYPE: 'Signature Type',
  KTOPL: 'Chart of Accounts',
  GLRRCTY: 'Record Type',
  SEGMENT: 'Segment',
  DOKOB: 'Linked Document Object',
  DOKST: 'Document Status',
  STATUS: 'Status',
  SICHT: 'Class View',
  OBJECTCLAS: 'Object Class',
  RSMTEAMNAM: 'Team Name',
  RSMTEAMTYP: 'Team Type',
  OC_ORGU_ID: 'Organizational Unit ID',
  OC_ORGU_TY: 'Organizational Unit Type',
  OC_ORG_ID: 'Organization ID',
  OC_ORG_TYP: 'Organization Type',
  '/PF1/ACGRP': 'In-House Bank Account Group',
  '/PF1/ALIAS': 'In-House Bank Alias',
  '/PF1/BKARE': 'In-House Bank Area',
  '/PF1/CUR': 'In-House Bank Currency',
  '/PF1/GLGRP': 'In-House Bank G/L Group',
};

const INDICATOR_CLASS = {
  success: 'bg-emerald-500',
  absent: 'bg-red-500',
  failed: 'bg-amber-500',
  ambiguous: 'bg-slate-400',
  unknown: 'bg-slate-400',
  locked: 'bg-slate-300',
};

function workflowState(ticket) {
  const state = ticket?.workflow_state;
  if (state && typeof state === 'object') return state;
  return {};
}

export function catalogRoleFollowUp(ticket) {
  return workflowState(ticket).catalog_role_follow_up || null;
}

/** A SAP BAPIRET2 row, serialized with varying key casing depending on the caller. */
function sapMessageText(item) {
  if (item === null || item === undefined) return '';
  if (typeof item !== 'object') return String(item);
  const text = item.message || item.MESSAGE || item.Message
    || item.text || item.TEXT || item.Text || '';
  if (text) return String(text);
  // No recognizable message field — fall back to a readable key:value list
  // instead of the useless "[object Object]".
  return Object.entries(item)
    .filter(([, v]) => v !== '' && v !== null && v !== undefined)
    .map(([k, v]) => `${k}=${v}`)
    .join(' ');
}

function readableDetail(value) {
  if (Array.isArray(value)) return value.map((item) => sapMessageText(item)).join('; ');
  if (value !== null && typeof value === 'object') return sapMessageText(value);
  const text = String(value ?? '').trim();
  if (!text || text === '[]' || text === '{}') return '';
  if (text.startsWith('inserted=')) {
    const inserted = text.match(/inserted=(\d+)/)?.[1] || '0';
    const deleted = text.match(/deleted=(\d+)/)?.[1] || '0';
    return `Added ${inserted} role-menu entries and removed ${deleted}.`;
  }
  if (text.startsWith('[')) {
    try {
      const parsed = JSON.parse(text);
      if (Array.isArray(parsed)) return parsed.map((item) => sapMessageText(item)).join('; ');
    } catch {
      return text;
    }
  }
  return text;
}

function orgKey(field) {
  return String(field || '').replace(/^\$/, '').toUpperCase();
}

function readinessKey(field) {
  const key = orgKey(field);
  if (['FCLM_BUKRS', 'FCLM_GSBER', 'FCLM_KOKRS', 'FCLM_PRCTR'].includes(key)) {
    return key.replace('FCLM_', '');
  }
  return key;
}

function isOrgPlaceholder(value) {
  return String(value || '').trim().startsWith('$');
}

function traceIndicator(row) {
  if (row.trace_indicator) return row.trace_indicator;
  if (row.trace_evidence === 'observed') return 'success';
  if (row.trace_evidence === 'not observed') return 'absent';
  if (row.trace_evidence === 'failed') return 'failed';
  return 'unknown';
}

function groupByObject(rows) {
  const groups = new Map();
  rows.forEach((row) => {
    const name = row.object || 'UNKNOWN';
    if (!groups.has(name)) groups.set(name, []);
    groups.get(name).push(row);
  });
  return [...groups.entries()];
}

function groupByField(rows) {
  const groups = new Map();
  rows.forEach((row) => {
    const name = row.field || '';
    if (!groups.has(name)) groups.set(name, []);
    groups.get(name).push(row);
  });
  return [...groups.entries()];
}

const DOWNSTREAM_NEXT = {
  AWAITING_TRANSPORT_RELEASE_APPROVAL: 'Next: Transport Release Approval. Record the decision below. HALO does not release the transport.',
  AWAITING_QA_CONFIRMATION: 'Next: QA Confirmation (QS4). Attach a screenshot of the QS4 import check.',
  AWAITING_FUT: 'Next: Functional User Testing (FUT). Attach the requester\'s test screenshot.',
  FUT_REMEDIATION_REQUIRED: 'FUT failed. Remediation is required before CAB.',
  AWAITING_CAB: 'Next: CAB Review & Approval. A description is required.',
  CAB_REJECTED: 'CAB rejected the change. It does not proceed to production.',
  AWAITING_PROD_CONFIRMATION: 'Next: Production Confirmation (PS4). Attach a screenshot of the production check.',
};

export function catalogDownstreamStatus(ticket, followUp) {
  const stage = String(ticket?.workflow_stage || '').toUpperCase();
  const state = ticket?.workflow_state && typeof ticket.workflow_state === 'object' ? ticket.workflow_state : {};
  const transportId = followUp?.handoff?.transport_id || followUp?.transport_id || 'the transport';
  if (stage === 'DONE') {
    const release = state.transport_release_approval || {};
    const prod = state.prod_confirmation || {};
    const when = prod.at ? ` on ${new Date(prod.at).toLocaleString()}` : '';
    return `Complete. Transport ${transportId} release was approved${release.actor ? ` by ${release.actor}` : ''}, and QA, FUT, CAB, and production confirmation were recorded${when}.`;
  }
  return DOWNSTREAM_NEXT[stage] || '';
}

export default function CatalogRoleFollowUp({
  ticket,
  orgSuggestions,
  onSubmit,
  submitting = false,
  canMutate = false,
}) {
  const followUp = catalogRoleFollowUp(ticket);
  const state = workflowState(ticket);
  const fioriV2 = Number(state.fiori_workflow_version || 0) >= 2;
  const downstreamStatus = catalogDownstreamStatus(ticket, followUp);
  const [traceFile, setTraceFile] = useState(null);
  const [traceSelectionError, setTraceSelectionError] = useState('');
  const [traceDetailsOpen, setTraceDetailsOpen] = useState(false);
  const traceInputRef = useRef(null);
  const storedCleanup = followUp?.correction_approval?.cleanup || [];
  const [unselected, setUnselected] = useState(() => Object.fromEntries(
    storedCleanup.filter((row) => row?.id).map((row) => [row.id, true]),
  ));
  const [orgValues, setOrgValues] = useState({});
  const [orgDraft, setOrgDraft] = useState({});
  const [orgValidation, setOrgValidation] = useState({});
  const [validationNote, setValidationNote] = useState('');
  const [justification, setJustification] = useState(followUp?.correction_approval?.justification || '');
  const [orgJustification, setOrgJustification] = useState('');
  const [openFieldEntries, setOpenFieldEntries] = useState({});
  const [openDraft, setOpenDraft] = useState({});
  const [deactivatedInstances, setDeactivatedInstances] = useState({});
  const [profileJustification, setProfileJustification] = useState('');
  const [testUser, setTestUser] = useState('');
  const [assignmentJustification, setAssignmentJustification] = useState('');
  const [openTrace, setOpenTrace] = useState('');
  if (!followUp) return null;
  const review = followUp.review || {};
  const menu = review.menu || {};
  const authorizations = review.authorizations || {};
  const outstanding = review.outstanding || {};
  const trace = review.trace || {};
  const profile = review.profile || {};
  const transport = review.transport || followUp.transport_verification || {};
  const refresh = followUp.refresh_execution || {};
  const merge = followUp.merge_execution || {};
  const stages = Array.isArray(followUp.stages) ? followUp.stages : [];
  const recommendations = review.recommendations || authorizations.recommendations || [];
  const proposalHash = review.proposal_hash || trace.proposal_hash;
  const stageStatus = (key) => stages.find((row) => row.key === key)?.status;
  const comparisonReady = stageStatus('pfcg_refresh') === 'done' && stageStatus('authorization_merge') === 'done';
  const authorizationChanges = [
    ...(authorizations.newly_added || []),
    ...(authorizations.broadened || []),
    ...(authorizations.changed || []),
  ];
  const impactRows = recommendations.filter((row) => row.attributed_to_new_application === true || ['newly_added', 'broadened', 'changed'].includes(row.status));
  const noAuthorizationDelta = comparisonReady && authorizationChanges.length === 0 && impactRows.length === 0;
  const existingAuthorizations = authorizations.existing || [];
  const unselectedIds = impactRows.filter((row) => unselected[row.id] === true && !isOrgPlaceholder(row.low)).map((row) => row.id);
  const orgLevels = outstanding.unmaintained_organizational_levels || [];
  const readinessOrgLevels = [...new Map(
    orgLevels.map((row) => [readinessKey(row.field), { ...row, field: readinessKey(row.field) }]),
  ).values()];
  const readinessBlocked = profile.blocker === 'WILDCARD_NOT_ALLOWED';
  const newMenu = menu.newly_added || [];
  const concreteRows = impactRows.filter((row) => String(row.low || '').trim() && !isOrgPlaceholder(row.low));
  const linkedOrgRows = impactRows.filter((row) => isOrgPlaceholder(row.low));
  const openRows = impactRows.filter((row) => !String(row.low || '').trim());
  const openFields = outstanding.open_fields || [];
  const cleanupReview = followUp.current_stage === 'cleanup_review';
  const cleanupChoice = cleanupReview || followUp.current_stage === 'authorization_maintenance';
  const readinessActive = followUp.current_stage === 'authorization_review';
  const readinessApproval = followUp.readiness_approval || {};
  const staleApproval = readinessApproval.status === 'approved' && !readinessApproval.approval_id;
  const readinessExecution = followUp.readiness_execution || {};
  const readinessFailure = readinessExecution.status && readinessExecution.verified === false ? readinessExecution : null;
  const readinessApplied = followUp.readiness_applied || null;
  const profileExecution = followUp.profile_execution || {};
  const profileFailure = followUp.current_stage === 'profile_verification' && profileExecution.phase === 'initial' && profileExecution.verified === false
    ? profileExecution
    : null;
  const initialProfileApplied = followUp.initial_profile_applied || null;
  const regenerationFailure = followUp.current_stage === 'profile_regeneration' && profileExecution.phase === 'regeneration' && profileExecution.verified === false
    ? profileExecution
    : null;
  const assignmentFailure = followUp.assignment_execution?.verified === false ? followUp.assignment_execution : null;
  const traceActive = ['trace_review', 'cleanup_review'].includes(followUp.current_stage);
  const traceAuditMissing = fioriV2 && Boolean(followUp.trace_upload) && followUp.trace_upload?.stored !== true;
  const baselineInstances = new Set(
    (followUp.review_input?.before_authorizations?.instances || [])
      .map((row) => `${row.object}|${row.auth}`),
  );
  const sapSource = orgSuggestions?.source === 'live_sap'
    ? `${orgSuggestions.system_id || 'SAP'} client ${orgSuggestions.client || ''}`.trim()
    : '';
  const finished = String(ticket?.workflow_stage || '').toUpperCase() === 'DONE';
  const visibleStages = stages.filter((stage) => !(fioriV2 && stage.status === 'skipped'));

  const submit = (action, extra = {}) => {
    if (!canMutate || submitting) return;
    onSubmit?.({ action, ...extra, evidenceFile: traceFile });
  };

  const chooseTraceFile = (event) => {
    const selected = event.target.files?.[0] || null;
    const extension = selected ? selected.name.slice(selected.name.lastIndexOf('.')).toLowerCase() : '';
    if (selected && !TRACE_EXTENSIONS.includes(extension)) {
      setTraceFile(null);
      setTraceSelectionError('Use an STAUTHTRACE or ST01 export: TXT, CSV, TSV, LOG, JSON, or XLSX.');
      event.target.value = '';
      return;
    }
    setTraceSelectionError('');
    setTraceFile(selected);
  };

  const chosenOrgValues = () => readinessOrgLevels.flatMap((row) => {
    const key = readinessKey(row.field);
    return (orgValues[key] || []).map((low) => {
      const candidate = (orgSuggestions?.fields?.[key]?.suggestions || [])
        .find((item) => item.value === low);
      return {
        field: key,
        low,
        related_value: candidate?.related_value || '',
      };
    });
  });

  const candidatesFor = (key) => {
    const rows = orgSuggestions?.fields?.[key]?.suggestions || [];
    const controllingAreas = orgValues.KOKRS || [];
    if (!controllingAreas.length || !['BUKRS', 'PRCTR'].includes(key)) return rows;
    return rows.filter((row) => !row.related_value || controllingAreas.includes(row.related_value));
  };

  const openFieldDecisions = () => {
    const decisions = [];
    const deactivated = new Set();
    openFields.forEach((row) => {
      const instance = `${row.object}|${row.auth}`;
      if (deactivatedInstances[instance]) {
        if (!deactivated.has(instance)) {
          deactivated.add(instance);
          decisions.push({ mode: 'deactivate_instance', object: row.object, auth: row.auth });
        }
        return;
      }
      const key = `${instance}|${row.field}`;
      const values = openFieldEntries[key] || [];
      if (values.length) {
        decisions.push({
          mode: 'maintain',
          object: row.object,
          auth: row.auth,
          field: row.field,
          values,
        });
      }
    });
    return decisions;
  };

  const addOpenValue = (row) => {
    const key = `${row.object}|${row.auth}|${row.field}`;
    const draft = openDraft[key] || {};
    const low = String(draft.low || '').trim();
    const high = row.interval_supported === false ? '' : String(draft.high || '').trim();
    if (!low || low.includes(',') || low.includes(';') || high.includes(',') || high.includes(';')) {
      setOrgValidation((current) => ({ ...current, [key]: 'Enter one exact value or interval. Do not paste a comma-separated list.' }));
      return;
    }
    setOpenFieldEntries((current) => ({
      ...current,
      [key]: [...(current[key] || []).filter((item) => item.low !== low || item.high !== high), { low, high }],
    }));
    setOpenDraft((current) => ({ ...current, [key]: { low: '', high: '' } }));
    setOrgValidation((current) => ({ ...current, [key]: '' }));
  };

  const addOrgValue = (key) => {
    const value = String(orgDraft[key] || '').trim();
    if (!value) return;
    const source = orgSuggestions?.fields?.[key] || {};
    const candidates = candidatesFor(key);
    if (candidates.length && !candidates.some((row) => row.value === value)) {
      setOrgValidation((current) => ({ ...current, [key]: 'Choose a value returned by SAP.' }));
      return;
    }
    if (!candidates.length && source.manual_entry_allowed !== true) {
      setOrgValidation((current) => ({ ...current, [key]: 'SAP value help is unavailable.' }));
      return;
    }
    setOrgValues((current) => ({
      ...current,
      [key]: [...new Set([...(current[key] || []), value])],
    }));
    setOrgDraft((current) => ({ ...current, [key]: '' }));
    setOrgValidation((current) => ({ ...current, [key]: '' }));
  };

  return (
    <section className="space-y-4" data-testid="catalog-role-follow-up">
      <header className="rounded-lg border bg-card px-4 py-3 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Role follow-up</p>
            <h3 className="text-base font-semibold" data-testid="catalog-role-impact-title">{finished ? 'Completed authorization outcome' : 'Authorization Impact'}</h3>
            <p className="mt-1 text-sm text-muted-foreground" data-testid="catalog-role-current-stage">
              {STAGE_LABELS[followUp.current_stage] || followUp.current_stage || 'PFCG refresh'}
              {' · '}{followUp.role || 'Target role'}
            </p>
          </div>
          <span className="rounded-full bg-muted px-3 py-1 text-xs font-medium">
            {finished ? 'Complete' : comparisonReady ? 'SAP comparison ready' : 'Waiting for SAP'}
          </span>
        </div>
        <p className="mt-2 text-sm">{finished ? downstreamStatus : (NEXT_ACTION[followUp.current_stage] || NEXT_ACTION.pfcg_refresh)}</p>
        {!finished && Array.isArray(followUp.technical_progress) && followUp.technical_progress.length > 0 && (
          <ol className="mt-3 space-y-1 text-sm" data-testid="catalog-technical-progress">
            {followUp.technical_progress.map((step) => (
              <li key={step.key}>
                <span className="font-medium">{step.label}</span>
                {': '}
                <span data-testid={`catalog-technical-${step.key}`}>{step.status}</span>
                {readableDetail(step.detail) ? <span className="text-muted-foreground"> — {readableDetail(step.detail)}</span> : null}
              </li>
            ))}
          </ol>
        )}
        {['ready_for_qa', 'awaiting_transport_release'].includes(followUp.handoff?.status) && (
          <div className="mt-3 space-y-1 text-sm" data-testid="catalog-qa-handoff">
            <p>Application {followUp.handoff.fiori_id || 'not recorded'} · catalog {followUp.handoff.catalog_id || 'not recorded'} · role {followUp.handoff.role}</p>
            <p>Profile {followUp.handoff.profile_name} verified.</p>
            {!fioriV2 && <p>{followUp.handoff.user_synchronization_note || 'User comparison was not performed and is not required for role-level transport readiness.'}</p>}
            <p>Transport {followUp.handoff.transport_id} objects verified.</p>
            {!finished && <p>{downstreamStatus}</p>}
          </div>
        )}
        <details className="mt-2 text-xs text-muted-foreground">
          <summary className="cursor-pointer">Technical status</summary>
          <div className="mt-2 space-y-1 pl-2" data-testid="catalog-role-stages">
            {visibleStages.map((stage) => (
              <p key={stage.key}>{STAGE_LABELS[stage.key] || stage.key}: {stage.status}</p>
            ))}
            <p data-testid="catalog-role-acgr">
              {transport.role_changed
                ? `R3TR ACGR is recorded on ${transport.transport_id || 'the shared request'}.`
                : `R3TR ACGR remains protected unless an approved role change requires it.`}
            </p>
          </div>
        </details>
      </header>
      <details className="space-y-4" open={!finished} data-testid="catalog-role-history">
        <summary className={finished ? 'cursor-pointer rounded-lg border bg-card px-4 py-3 text-sm font-medium' : 'hidden'}>
          Technical authorization history
        </summary>
      {review.menu && !comparisonReady && (
        <p data-testid="catalog-role-comparison-pending">
          Authorization impact appears after the PFCG refresh and authorization comparison succeed.
        </p>
      )}
      {comparisonReady && noAuthorizationDelta && (
        <p data-testid="catalog-role-no-delta">No new authorization changes detected</p>
      )}
      {comparisonReady && newMenu.length > 0 && (
        <details className="rounded-lg border bg-card px-4 py-3 text-sm" data-testid="catalog-role-menu-added">
          <summary className="cursor-pointer font-medium">{newMenu.length} new menu services</summary>
          <p className="mt-2 text-muted-foreground">
            {newMenu.map((row) => row.name || row.obj_name || row.catalog_id).filter(Boolean).join(', ')}
          </p>
        </details>
      )}
      {comparisonReady && impactRows.length > 0 && (
        <div className="space-y-3" data-testid="catalog-role-objects">
          <div className="grid gap-2 sm:grid-cols-3">
            <div className="rounded-lg border bg-card p-3">
              <p className="text-lg font-semibold">{concreteRows.length}</p>
              <p className="text-xs text-muted-foreground">Concrete field values</p>
            </div>
            <div className="rounded-lg border bg-card p-3">
              <p className="text-lg font-semibold">{linkedOrgRows.length}</p>
              <p className="text-xs text-muted-foreground">Linked organizational fields</p>
            </div>
            <div className="rounded-lg border bg-card p-3">
              <p className="text-lg font-semibold">{openRows.length}</p>
              <p className="text-xs text-muted-foreground">Open maintenance fields</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 px-1 text-xs text-muted-foreground" data-testid="catalog-role-trace-legend">
            <span><i className="mr-1 inline-block h-2.5 w-2.5 rounded-full bg-emerald-500" />Successful check</span>
            <span><i className="mr-1 inline-block h-2.5 w-2.5 rounded-full bg-red-500" />Not seen in this trace</span>
            <span><i className="mr-1 inline-block h-2.5 w-2.5 rounded-full bg-amber-500" />Check failed</span>
            <span><i className="mr-1 inline-block h-2.5 w-2.5 rounded-full bg-slate-400" />No trace or ambiguous</span>
          </div>
          {groupByObject(concreteRows).map(([objectName, objectRows]) => (
            <details key={objectName} className="rounded-lg border bg-card shadow-sm" data-testid={`catalog-role-object-${objectName}`}>
              <summary className="cursor-pointer px-4 py-3 font-medium">
                {objectName}
                {OBJECT_TEXT[objectName] ? ` — ${OBJECT_TEXT[objectName]}` : ''}
                <span className="ml-2 text-xs font-normal text-muted-foreground">{objectRows.length} new value{objectRows.length === 1 ? '' : 's'}</span>
              </summary>
              <div className="space-y-3 border-t px-4 py-3">
                {groupByField(objectRows).map(([field, fieldRows]) => {
                  const protectedValues = existingAuthorizations.filter((row) => row.object === objectName && row.field === field);
                  return (
                    <div key={`${objectName}-${field}`}>
                      <p className="text-sm font-medium">{field}{FIELD_TEXT[field] ? ` · ${FIELD_TEXT[field]}` : ''}</p>
                      <ul className="space-y-1">
                        {protectedValues.map((row) => (
                          <li key={`locked-${row.low}`} className="flex items-center gap-2 text-sm text-muted-foreground" data-testid={`catalog-role-locked-${objectName}-${field}-${row.low}`}>
                            <span className="inline-block h-2.5 w-2.5 rounded-full bg-slate-300" />
                            {row.low} locked
                          </li>
                        ))}
                        {fieldRows.map((row) => {
                          const indicator = traceIndicator(row);
                          const placeholder = isOrgPlaceholder(row.low);
                          return (
                            <li key={row.id} className="rounded-md border bg-background px-3 py-2 text-sm" data-testid={`catalog-role-value-${row.id}`}>
                              <div className="flex flex-wrap items-center justify-between gap-3">
                                <span className="flex items-center gap-2">
                                <button
                                  type="button"
                                  className={`h-2.5 w-2.5 rounded-full ${INDICATOR_CLASS[indicator] || INDICATOR_CLASS.unknown}`}
                                  aria-label={`Trace evidence for ${objectName} ${field} ${row.low}`}
                                  data-testid={`catalog-role-trace-indicator-${row.id}`}
                                  onClick={() => setOpenTrace(openTrace === row.id ? '' : row.id)}
                                />
                                <span className="font-mono">{row.low}</span>
                                </span>
                                {!placeholder && cleanupChoice && (
                                  <span className="flex gap-4">
                                    <label>
                                      <input
                                        type="radio"
                                        name={`decision-${row.id}`}
                                        checked={unselected[row.id] !== true}
                                        disabled={!canMutate}
                                        onChange={() => setUnselected((current) => ({ ...current, [row.id]: false }))}
                                      />
                                      {' '}Select
                                    </label>
                                    <label>
                                      <input
                                        type="radio"
                                        name={`decision-${row.id}`}
                                        data-testid={`catalog-role-unselect-${row.id}`}
                                        checked={unselected[row.id] === true}
                                        disabled={!canMutate}
                                        onChange={() => setUnselected((current) => ({ ...current, [row.id]: true }))}
                                      />
                                      {' '}Unselect
                                    </label>
                                  </span>
                                )}
                              </div>
                              {openTrace === row.id && (
                                <div className="mt-1 rounded border bg-muted/40 p-2 text-xs" data-testid={`catalog-role-trace-detail-${row.id}`}>
                                  {row.trace_reference && <p>Reference evidence from another user. This is not proof of this role&apos;s access.</p>}
                                  {(row.matching_checks || []).length === 0 && <p>No trace entry matched this value.</p>}
                                  {(row.matching_checks || []).map((check, index) => (
                                    <p key={`${check.timestamp || index}`}>
                                      {check.object} {check.field} {check.value || check.low} {check.result} {check.timestamp || ''}
                                    </p>
                                  ))}
                                </div>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </details>
          ))}
          <p className="px-1 text-sm text-muted-foreground">Unselect proposes removal of that value only. Existing values stay protected, and SAP is unchanged until separate approval and application.</p>
        </div>
      )}
      {comparisonReady && existingAuthorizations.length > 0 && (
        <details className="rounded-lg border px-4 py-3 text-sm" data-testid="catalog-role-existing">
          <summary className="cursor-pointer" data-testid="catalog-role-existing-toggle">
            Protected existing permissions ({existingAuthorizations.length})
          </summary>
          <p className="mt-2 text-muted-foreground" data-testid="catalog-role-auth-existing">
            Existing permissions are excluded from selection and cannot be removed by this review.
          </p>
        </details>
      )}
      {comparisonReady && review.menu && traceActive && (
        <section className="space-y-4 rounded-lg border bg-card px-4 py-4 shadow-sm" data-testid="catalog-role-task-validation">
          <div>
            <h3 className="font-semibold">Authorization review</h3>
            <p className="text-sm text-muted-foreground" data-testid="catalog-role-trace-request">
              {fioriV2
                ? 'Review the newly introduced authorization values. You may upload STAUTHTRACE as supporting evidence, but no user ID or trace is required. Values not observed in a trace remain until a reviewer explicitly selects them for removal.'
                : `User ${followUp.test_user?.username || 'not identified'} has effective profile ${followUp.test_user?.effective_profile || followUp.initial_user_comparison?.profile_name || 'not verified'}. Exercise ${followUp.fiori_id || 'the application'} and upload that user's actual STAUTHTRACE.`}
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="catalog-role-task-note">Business activities tested</Label>
            <Textarea
              id="catalog-role-task-note"
              data-testid="catalog-role-task-note"
              className="min-h-28 w-full"
              value={validationNote}
              disabled={!canMutate}
              onChange={(event) => setValidationNote(event.target.value)}
              placeholder="Describe the business activities that were tested, such as displaying, creating, or changing a bank account."
            />
          </div>
          <TraceUploadCard
            canMutate={canMutate}
            submitting={submitting}
            traceFile={traceFile}
            selectionError={traceSelectionError}
            receipt={followUp.trace_upload}
            serverError={followUp.last_error?.code === 'TRACE_EVIDENCE_INVALID' ? followUp.last_error : null}
            detailsOpen={traceDetailsOpen}
            inputRef={traceInputRef}
            onChoose={chooseTraceFile}
            onUpload={() => submit('record_catalog_trace')}
            onReplace={() => {
              setTraceFile(null);
              setTraceSelectionError('');
              if (traceInputRef.current) traceInputRef.current.value = '';
              traceInputRef.current?.click();
            }}
            onToggleDetails={() => setTraceDetailsOpen((current) => !current)}
          />
        </section>
      )}
      {traceAuditMissing && !traceActive && (
        <section className="space-y-3 rounded-lg border bg-card px-4 py-4 shadow-sm" data-testid="catalog-role-trace-audit">
          <div>
            <h3 className="font-semibold">Save authorization trace for audit</h3>
            <p className="text-sm text-muted-foreground">
              The trace analysis is already recorded. Upload the original file once to retain it in this ticket&apos;s Documents section without reopening the authorization review.
            </p>
          </div>
          <TraceUploadCard
            canMutate={canMutate}
            submitting={submitting}
            traceFile={traceFile}
            selectionError={traceSelectionError}
            receipt={followUp.trace_upload}
            serverError={null}
            detailsOpen={traceDetailsOpen}
            inputRef={traceInputRef}
            onChoose={chooseTraceFile}
            onUpload={() => submit('record_catalog_trace')}
            onReplace={() => {
              setTraceFile(null);
              setTraceSelectionError('');
              if (traceInputRef.current) traceInputRef.current.value = '';
              traceInputRef.current?.click();
            }}
            onToggleDetails={() => setTraceDetailsOpen((current) => !current)}
          />
        </section>
      )}
      <section className="space-y-3 rounded-lg border bg-card px-4 py-4 shadow-sm" data-testid="catalog-role-readiness">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h3 className="font-semibold">PFCG &amp; SU24 Authorization Preparation</h3>
            <p className="text-sm text-muted-foreground">Maintain required organizational values before HALO automatically generates the initial profile.</p>
          </div>
          {sapSource && <span className="rounded-full bg-muted px-3 py-1 text-xs">Live {sapSource}</span>}
        </div>
        {readinessActive && (readinessBlocked || (outstanding.unmaintained_organizational_level_count || 0) + (outstanding.open_field_count || 0) > 0) && (
          <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="catalog-role-readiness-block">
            {readinessBlocked
              ? (profile.detail || 'A wildcard value must be removed before profile generation.')
              : 'Unresolved organizational levels and blank authorization fields can remain empty. An empty value does not grant access and is not a profile-generation prerequisite.'}
          </p>
        )}
        {readinessActive && comparisonReady && readinessOrgLevels.map((row) => {
          const key = readinessKey(row.field);
          const candidates = candidatesFor(key);
          const source = orgSuggestions?.fields?.[key] || {};
          const selected = orgValues[key] || [];
          return (
            <div key={key} className="rounded-md border p-3" data-testid={`catalog-role-org-input-${key}`}>
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="text-sm font-medium">{FIELD_TEXT[key] || key} <span className="font-mono text-xs text-muted-foreground">${key}</span></p>
                {candidates[0]?.source && <span className="text-xs text-muted-foreground">{candidates[0].source}</span>}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">Add one or more exact SAP values. Each value is stored separately for this organizational variable.</p>
              {key === 'PRCTR' && !(orgValues.KOKRS || []).length && (
                <p className="mt-2 text-xs text-muted-foreground">Choose a controlling area first to filter valid profit centers.</p>
              )}
              <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                <input
                  className="min-w-0 flex-1 rounded-md border bg-background px-3 py-2 text-sm"
                aria-label={key}
                  list={`catalog-role-org-options-${key}`}
                  value={orgDraft[key] || ''}
                disabled={!canMutate || !readinessActive}
                  placeholder={candidates.length ? 'Search SAP suggestions' : 'Enter a value for SAP validation'}
                  onChange={(event) => setOrgDraft((current) => ({ ...current, [key]: event.target.value }))}
                />
                <datalist id={`catalog-role-org-options-${key}`}>
                {candidates.map((candidate) => (
                    <option key={`${candidate.value}-${candidate.related_value || ''}`} value={candidate.value}>
                      {candidate.description}
                    </option>
                ))}
                </datalist>
                <Button type="button" variant="outline" disabled={!canMutate || !readinessActive || !String(orgDraft[key] || '').trim()} onClick={() => addOrgValue(key)}>
                  Add
                </Button>
              </div>
              {orgValidation[key] && <p className="mt-1 text-xs text-destructive">{orgValidation[key]}</p>}
              {selected.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {selected.map((value) => (
                    <button
                      key={value}
                      type="button"
                      className="rounded-full bg-muted px-3 py-1 text-xs"
                      disabled={!canMutate || !readinessActive}
                      onClick={() => setOrgValues((current) => ({
                        ...current,
                        [key]: (current[key] || []).filter((item) => item !== value),
                      }))}
                    >
                      {value} ×
                    </button>
                  ))}
                </div>
              )}
              {!candidates.length && orgSuggestions && (
                <p className="mt-2 text-xs text-muted-foreground">
                  {source.manual_entry_allowed
                    ? 'SAP returned no value help. Manual entry will be revalidated before application.'
                    : 'No SAP suggestions are currently available.'}
                </p>
              )}
            </div>
          );
        })}
        {readinessActive && comparisonReady && openFields.length > 0 && (
          <div className="space-y-3" data-testid="catalog-role-open-fields">
            <div>
              <p className="text-sm font-medium">Unmaintained authorization fields</p>
              <p className="text-xs text-muted-foreground">
                These fields came from SAP without a proposed value. Leave a field blank, add one or more exact values, or deactivate the introduced instance. Multiple values stay on the same authorization instance. Wildcards are rejected.
              </p>
            </div>
            {groupOpenFields(openFields).map(([instance, rows]) => {
              const [objectName, auth] = instance.split('|');
              const deactivated = deactivatedInstances[instance] === true;
              const canDeactivate = !baselineInstances.has(instance);
              return (
                <details key={instance} className="rounded-md border bg-background" data-testid={`catalog-role-open-instance-${objectName}-${auth}`}>
                  <summary className="cursor-pointer px-3 py-2 text-sm font-medium">
                    {OBJECT_TEXT[objectName] || objectName} · {objectName} · {rows.length} unmaintained field{rows.length === 1 ? '' : 's'}
                  </summary>
                  <div className="space-y-3 border-t p-3">
                    {rows.map((row) => {
                      const key = `${instance}|${row.field}`;
                      const draft = openDraft[key] || {};
                      const selected = openFieldEntries[key] || [];
                      const context = (row.instance_values || []).map((value) => `${value.field}=${value.low}`).join(', ');
                      return (
                        <div key={key} className="space-y-1">
                          <Label htmlFor={`open-${key}`}>{FIELD_TEXT[row.field] || row.field} <span className="font-mono text-xs text-muted-foreground">{row.field}</span></Label>
                          <p className="text-xs text-muted-foreground">Unmaintained · optional for profile generation · {row.suggestion_backed ? 'SAP suggestions' : 'manual entry with SAP field validation'}{row.interval_supported === false ? ' · discrete values only' : ' · interval supported'}</p>
                          {context && <p className="text-xs text-muted-foreground">Existing instance values: {context}</p>}
                          <div className="flex flex-col gap-2 sm:flex-row">
                            <input
                              id={`open-${key}`}
                              className="min-w-0 flex-1 rounded-md border bg-background px-3 py-2 text-sm"
                              data-testid={`catalog-role-open-field-${objectName}-${row.field}`}
                              value={draft.low || ''}
                              disabled={!canMutate || !readinessActive || deactivated}
                              placeholder="Enter one approved SAP value"
                              onChange={(event) => setOpenDraft((current) => ({ ...current, [key]: { ...draft, low: event.target.value } }))}
                            />
                            {row.interval_supported !== false && (
                              <input
                                className="min-w-0 flex-1 rounded-md border bg-background px-3 py-2 text-sm"
                                aria-label={`${row.field} high`}
                                value={draft.high || ''}
                                disabled={!canMutate || !readinessActive || deactivated}
                                placeholder="High, only for an interval"
                                onChange={(event) => setOpenDraft((current) => ({ ...current, [key]: { ...draft, high: event.target.value } }))}
                              />
                            )}
                            <Button type="button" variant="outline" disabled={!canMutate || !readinessActive || deactivated || !String(draft.low || '').trim()} onClick={() => addOpenValue(row)}>
                              Add
                            </Button>
                          </div>
                          {orgValidation[key] && <p className="text-xs text-destructive">{orgValidation[key]}</p>}
                          {selected.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {selected.map((value) => (
                                <button
                                  key={`${value.low}-${value.high}`}
                                  type="button"
                                  className="rounded-full bg-muted px-3 py-1 text-xs"
                                  disabled={!canMutate || !readinessActive}
                                  onClick={() => setOpenFieldEntries((current) => ({
                                    ...current,
                                    [key]: (current[key] || []).filter((item) => item.low !== value.low || item.high !== value.high),
                                  }))}
                                >
                                  {value.high ? `${value.low}–${value.high}` : value.low} ×
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {canDeactivate && (
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={deactivated}
                          disabled={!canMutate || !readinessActive}
                          onChange={(event) => setDeactivatedInstances((current) => ({ ...current, [instance]: event.target.checked }))}
                        />
                        Deactivate this newly introduced authorization instance
                      </label>
                    )}
                  </div>
                </details>
              );
            })}
          </div>
        )}
        {readinessActive && comparisonReady && keyLinked(impactRows) && (
          <p className="text-sm" data-testid="catalog-role-org-link">FCLM_BUKRS stays linked to $BUKRS and is maintained with the company code.</p>
        )}
        {readinessActive && (
          <p className="text-xs text-muted-foreground">Suggestions are read from the connected SAP system and client. Selection does not write SAP until you choose Approve &amp; Apply Role Readiness.</p>
        )}
        {readinessActive && staleApproval && (
          <p className="rounded-md bg-muted px-3 py-2 text-sm" data-testid="catalog-role-readiness-superseded-notice">
            An earlier Role Readiness approval was never applied. Its values are not reused. Select the values and enter the justification again; the earlier approval stays in the audit history as superseded.
          </p>
        )}
        {readinessActive && readinessFailure && (
          <p className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive" role="alert" data-testid="catalog-role-readiness-failure">
            Role Readiness was not applied: {readinessFailure.status}. {readableDetail(readinessFailure.detail)}
            {' '}A retry reads SAP back first and never writes the same values twice.
          </p>
        )}
        {initialProfileApplied && (
          <div className="space-y-1 rounded-md border bg-muted/30 px-3 py-3 text-sm" data-testid="catalog-role-profile-summary">
            <p className="font-medium">Initial profile {initialProfileApplied.profile_name} verified in AGR_1016</p>
            <p>{initialProfileApplied.justification}</p>
            <p className="text-xs text-muted-foreground">{initialProfileApplied.detail}</p>
          </div>
        )}
        {readinessApplied && (
          <div className="space-y-2 rounded-md border bg-muted/30 px-3 py-3 text-sm" data-testid="catalog-role-readiness-summary">
            <p className="font-medium">
              Role Readiness applied and verified in SAP
              {readinessApplied.applied_at ? ` · ${new Date(readinessApplied.applied_at).toLocaleString()}` : ''}
            </p>
            <p className="text-xs text-muted-foreground">{readinessApplied.detail}</p>
            {(readinessApplied.org_values || []).length > 0 ? (
              <ul className="space-y-1">
                {groupAppliedOrg(readinessApplied.org_values).map(([field, values]) => (
                  <li key={field} data-testid={`catalog-role-readiness-applied-${field}`}>
                    {FIELD_TEXT[field] || field} <span className="font-mono text-xs text-muted-foreground">${field}</span>: <span className="font-mono">{values.join(', ')}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p>No organizational values were maintained.</p>
            )}
            {(readinessApplied.open_field_operations || []).length > 0 ? (
              <ul className="space-y-1">
                {readinessApplied.open_field_operations.map((op) => (
                  <li key={`${op.mode}-${op.object}-${op.auth}-${op.field}-${op.low}-${op.high}`} className="font-mono text-xs">
                    {op.mode === 'remove_object' ? `Deactivated ${op.object} ${op.auth}` : `${op.object} ${op.field} = ${op.high ? `${op.low}–${op.high}` : op.low}`}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-muted-foreground">Optional authorization fields were left blank.</p>
            )}
            <p><span className="text-muted-foreground">Justification:</span> {readinessApplied.justification}</p>
            <p className="text-xs text-muted-foreground">Approved by {readinessApplied.actor}{readinessApplied.transport_id ? ` · recorded on ${readinessApplied.transport_id}` : ''}</p>
          </div>
        )}
      </section>
      {refresh.status && (
        <p className="text-sm" data-testid="catalog-role-refresh-result">
          {refresh.status}. {readableDetail(refresh.detail)
            || (['APPLIED', 'ALREADY_CURRENT', 'RECONCILED_APPLIED'].includes(refresh.status)
              ? 'PFCG refresh was confirmed in SAP.'
              : 'SAP did not confirm the PFCG refresh.')}
        </p>
      )}
      {merge.status && (
        <p className="text-sm" data-testid="catalog-role-merge-result">
          {merge.status}. {readableDetail(merge.detail)
            || (['APPLIED', 'ALREADY_CURRENT', 'RECONCILED_APPLIED'].includes(merge.status)
              ? 'Authorization defaults were merged and verified in SAP.'
              : 'SAP did not confirm the authorization merge.')}
        </p>
      )}
      {followUp.correction_execution?.status && (
        <p data-testid="catalog-role-correction-result">
          {followUp.correction_execution.status}. {readableDetail(followUp.correction_execution.detail) || 'Approved cleanup was recorded.'}
        </p>
      )}
      {followUp.org_execution?.status && (
        <p data-testid="catalog-role-org-result">{followUp.org_execution.status}. {readableDetail(followUp.org_execution.detail)}</p>
      )}
      {followUp.last_error?.code && (
        <p data-testid="catalog-role-error">{followUp.last_error.code}</p>
      )}
      </details>
      {!finished && <div className="rounded-lg border bg-card p-4">
        <p className="mb-3 text-sm font-medium">Next action</p>
        {['ready_for_qa', 'awaiting_transport_release'].includes(followUp.handoff?.status) && (
          <p data-testid="catalog-role-next-action">{downstreamStatus}</p>
        )}
        <div className="flex flex-wrap gap-2">
        {!comparisonReady && (
          <Button type="button" data-testid="catalog-role-load" disabled={!canMutate || submitting} onClick={() => submit('prepare_catalog_role_review')}>
            Load role comparison
          </Button>
        )}
        {(followUp.current_stage === 'pfcg_refresh' || followUp.current_stage === 'authorization_merge') && (
          <Button type="button" variant="outline" data-testid="catalog-role-refresh" disabled={!canMutate || submitting} onClick={() => submit(followUp.current_stage === 'authorization_merge' ? 'execute_authorization_merge' : 'execute_catalog_role_refresh', { confirmation: 'EXECUTE' })}>
            Retry role maintenance
          </Button>
        )}
        {readinessActive && comparisonReady && (
          <div className="w-full space-y-2">
            <Label htmlFor="catalog-role-readiness-justification">Justification</Label>
            <Textarea
              id="catalog-role-readiness-justification"
              data-testid="catalog-role-readiness-justification"
              value={orgJustification}
              disabled={!canMutate}
              placeholder="Why are these organizational and open-field decisions appropriate?"
              onChange={(event) => setOrgJustification(event.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Your selected values are validated against live SAP, written once, read back, and recorded on the transport. Only a verified read-back counts as success.
            </p>
            <Button
              type="button"
              data-testid="catalog-role-approve-apply-readiness"
              disabled={!canMutate || submitting || readinessBlocked || !orgJustification.trim()}
              onClick={() => submit('approve_and_apply_role_readiness', {
                confirmation: 'APPROVE_AND_APPLY',
                justification: orgJustification,
                org_values: chosenOrgValues(),
                open_fields: openFieldDecisions(),
              })}
            >
              {submitting ? 'Applying in SAP…' : 'Approve & Apply Role Readiness'}
            </Button>
          </div>
        )}
        {followUp.current_stage === 'profile_verification' && !fioriV2 && (
          <div className="w-full space-y-2">
            <Label htmlFor="catalog-role-profile-justification">Justification</Label>
            <Textarea
              id="catalog-role-profile-justification"
              data-testid="catalog-role-profile-justification"
              value={profileJustification}
              disabled={!canMutate}
              placeholder="Why should the initial profile be generated now?"
              onChange={(event) => setProfileJustification(event.target.value)}
            />
            {profileFailure && (
              <p className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive" role="alert" data-testid="catalog-role-profile-failure">
                The initial profile was not verified: {profileFailure.status}. {readableDetail(profileFailure.detail)}
              </p>
            )}
            <p className="text-xs text-muted-foreground">This runs SUPRN_PROFILE_BATCH once and counts as success only after AGR_1016 contains the generated profile.</p>
            <Button
              type="button"
              data-testid="catalog-role-approve-generate-profile"
              disabled={!canMutate || submitting || readinessBlocked || !profileJustification.trim()}
              onClick={() => submit('approve_and_generate_profile', {
                confirmation: 'APPROVE_AND_GENERATE',
                phase: 'initial',
                justification: profileJustification,
              })}
            >
              {submitting ? 'Generating profile…' : 'Approve & Generate Initial Profile'}
            </Button>
          </div>
        )}
        {followUp.current_stage === 'profile_verification' && fioriV2 && (
          <div className="w-full space-y-2">
            {profileFailure && (
              <p className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive" role="alert" data-testid="catalog-role-profile-failure">
                The automatic initial profile generation was not verified: {profileFailure.status}. {readableDetail(profileFailure.detail)}
              </p>
            )}
            <Button
              type="button"
              data-testid="catalog-role-retry-profile"
              disabled={!canMutate || submitting || readinessBlocked}
              onClick={() => submit('approve_and_generate_profile', {
                confirmation: 'APPROVE_AND_GENERATE',
                phase: 'initial',
              })}
            >
              {submitting ? 'Retrying profile generation…' : 'Retry Automatic Profile Generation'}
            </Button>
          </div>
        )}
        {!fioriV2 && followUp.current_stage === 'user_assignment' && !followUp.test_user?.username && (
          <div className="flex w-full flex-col gap-2 sm:flex-row">
            <input className="min-w-0 flex-1 rounded-md border bg-background px-3 py-2 text-sm" aria-label="SAP test user" value={testUser} disabled={!canMutate} placeholder="SAP user ID" onChange={(event) => setTestUser(event.target.value.toUpperCase())} />
            <Button type="button" data-testid="catalog-role-identify-test-user" disabled={!canMutate || submitting || !testUser.trim()} onClick={() => submit('identify_catalog_test_user', { test_user: testUser })}>
              Check existing access
            </Button>
          </div>
        )}
        {!fioriV2 && followUp.current_stage === 'user_assignment' && followUp.test_user?.username && followUp.test_user?.role_assigned !== true && (
          <div className="w-full space-y-2">
            <p className="text-sm" data-testid="catalog-role-assignment-needed">{followUp.test_user.username} does not have {followUp.role}. Assignment is optional and only for an explicitly authorized user.</p>
            <Textarea value={assignmentJustification} disabled={!canMutate} placeholder="Why should this user receive the test role?" onChange={(event) => setAssignmentJustification(event.target.value)} />
            {assignmentFailure && (
              <p className="text-sm text-destructive" role="alert" data-testid="catalog-role-assignment-failure">
                The role was not assigned: {assignmentFailure.status}. {readableDetail(assignmentFailure.detail)}
              </p>
            )}
            <Button
              type="button"
              data-testid="catalog-role-approve-assign-role"
              disabled={!canMutate || submitting || !assignmentJustification.trim()}
              onClick={() => submit('approve_and_assign_test_role', {
                confirmation: 'APPROVE_AND_ASSIGN',
                justification: assignmentJustification,
              })}
            >
              {submitting ? 'Assigning role…' : 'Assign role only if authorized'}
            </Button>
          </div>
        )}
        {!fioriV2 && followUp.current_stage === 'user_comparison' && (
          <div className="w-full space-y-2">
            <p className="text-xs text-muted-foreground" data-testid="catalog-role-comparison-user">{followUp.test_user?.username} already has {followUp.role}. Profile {followUp.test_user?.effective_profile || 'the generated profile'} is not yet effective. User comparison does not generate the profile or change role authorizations.</p>
            <Button type="button" data-testid="catalog-role-user-comparison" disabled={!canMutate || submitting} onClick={() => submit('run_catalog_user_comparison', { confirmation: 'RUN_USER_COMPARISON' })}>
              Run user comparison
            </Button>
          </div>
        )}
        {(cleanupReview || followUp.current_stage === 'authorization_maintenance') && comparisonReady && (fioriV2 ? unselectedIds.length > 0 : !noAuthorizationDelta) && (
          <div className="w-full space-y-2">
            {unselectedIds.length > 0 && (
              <div className="space-y-1">
                <Label htmlFor="catalog-role-justification">Reason for removing the unselected values</Label>
                <Textarea
                  id="catalog-role-justification"
                  data-testid="catalog-role-justification"
                  value={justification}
                  disabled={!canMutate}
                  placeholder="Why should these new values be removed?"
                  onChange={(event) => setJustification(event.target.value)}
                />
              </div>
            )}
            <Button
              type="button"
              variant="outline"
              data-testid="catalog-role-approve-apply-cleanup"
              disabled={!canMutate || submitting || !proposalHash || (unselectedIds.length > 0 && !justification.trim())}
              onClick={() => submit('approve_and_apply_cleanup', {
                confirmation: 'APPROVE_AND_APPLY',
                proposal_hash: proposalHash,
                selected_ids: unselectedIds,
                justification,
              })}
            >
              {submitting ? 'Applying cleanup…' : 'Approve & Apply Cleanup'}
            </Button>
          </div>
        )}
        {cleanupReview && (fioriV2 ? unselectedIds.length === 0 : noAuthorizationDelta) && (
          <Button type="button" variant="outline" data-testid="catalog-role-no-cleanup" disabled={!canMutate || submitting || !review.menu} onClick={() => submit('record_no_authorization_cleanup', { note: validationNote })}>
            Continue Without Cleanup
          </Button>
        )}
        {followUp.technical_progress?.some((step) => step.status === 'failed') && (
          <Button type="button" data-testid="catalog-role-continue-technical" disabled={!canMutate || submitting} onClick={() => submit('continue_catalog_technical_completion', { confirmation: 'CONTINUE' })}>
            {submitting ? 'Continuing…' : 'Continue from the failed step'}
          </Button>
        )}
        {!fioriV2 && !followUp.technical_progress && followUp.current_stage === 'profile_regeneration' && (
          <div className="w-full space-y-2">
            <Textarea
              data-testid="catalog-role-regeneration-justification"
              value={profileJustification}
              disabled={!canMutate}
              placeholder="Why should the profile be regenerated after cleanup?"
              onChange={(event) => setProfileJustification(event.target.value)}
            />
            {regenerationFailure && (
              <p className="text-sm text-destructive" role="alert" data-testid="catalog-role-regeneration-failure">
                Profile regeneration was not verified: {regenerationFailure.status}.
              </p>
            )}
            <Button
              type="button"
              data-testid="catalog-role-approve-regenerate-profile"
              disabled={!canMutate || submitting || readinessBlocked || !profileJustification.trim()}
              onClick={() => submit('approve_and_generate_profile', {
                confirmation: 'APPROVE_AND_GENERATE',
                phase: 'regeneration',
                justification: profileJustification,
              })}
            >
              {submitting ? 'Regenerating profile…' : 'Approve & Regenerate Profile'}
            </Button>
          </div>
        )}
        {!fioriV2 && !followUp.technical_progress && followUp.current_stage === 'final_user_comparison' && (
          <div className="w-full space-y-2">
            <p className="text-xs text-muted-foreground">Final user comparison is separate from profile regeneration.</p>
            <Button type="button" data-testid="catalog-role-final-user-comparison" disabled={!canMutate || submitting} onClick={() => submit('run_catalog_user_comparison', { confirmation: 'RUN_USER_COMPARISON' })}>
              Run final user comparison
            </Button>
          </div>
        )}
        {!followUp.technical_progress && followUp.current_stage === 'transport_verification' && <Button type="button" variant="outline" data-testid="catalog-role-verify-transport" disabled={!canMutate || submitting} onClick={() => submit('verify_catalog_transport')}>
          Verify transport
        </Button>}
        </div>
      </div>}
    </section>
  );
}

function TraceUploadCard({
  canMutate,
  submitting,
  traceFile,
  selectionError,
  receipt,
  serverError,
  detailsOpen,
  inputRef,
  onChoose,
  onUpload,
  onReplace,
  onToggleDetails,
}) {
  const status = selectionError ? 'invalid_file' : (receipt?.status || '');
  const statusText = {
    parsed: 'Analyzed',
    no_checks: 'No checks found',
    failed: 'Could not analyze',
    invalid_file: 'File not accepted',
  }[status] || 'Not uploaded';
  return (
    <div className="space-y-3 rounded-md border bg-background p-3" data-testid="catalog-role-trace">
      <div>
        <p className="text-sm font-medium">Optional SAP trace</p>
        <p className="text-xs text-muted-foreground">A STAUTHTRACE or ST01 export may support the review, but no user identity is required. Matching stays at the individual newly introduced value and never removes anything automatically.</p>
      </div>
      <label
        className="flex cursor-pointer flex-col gap-1 rounded-md border border-dashed border-muted-foreground/40 bg-muted/30 px-3 py-3 text-sm hover:border-primary/50"
        title={permissionDeniedTitle(canMutate, 'upload a trace')}
      >
        <span className="font-medium">{traceFile ? traceFile.name : 'Choose a trace export'}</span>
        <span className="text-xs text-muted-foreground">TXT, CSV, TSV, LOG, JSON, or XLSX</span>
        <input
          ref={inputRef}
          type="file"
          accept={TRACE_EXTENSIONS.join(',')}
          className="sr-only"
          data-testid="catalog-role-trace-file"
          disabled={!canMutate}
          onChange={onChoose}
        />
      </label>
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          data-testid="catalog-role-upload-trace"
          disabled={!canMutate || submitting || !traceFile}
          onClick={onUpload}
        >
          Upload &amp; Analyze
        </Button>
        <Button type="button" variant="outline" data-testid="catalog-role-replace-trace" disabled={!canMutate || submitting} onClick={onReplace}>
          Replace file
        </Button>
      </div>
      {(receipt || selectionError || serverError) && (
        <div className="space-y-1 text-sm" data-testid="catalog-role-trace-result">
          <p data-testid="catalog-role-trace-status">{statusText}{receipt?.filename ? ` · ${receipt.filename}` : ''}</p>
          {receipt?.status === 'parsed' && (
            <p data-testid="catalog-role-trace-summary">
              {receipt.check_count} checks, {receipt.matched_count} authorization values matched.
            </p>
          )}
          {receipt?.stored && (
            <p className="text-xs text-muted-foreground" data-testid="catalog-role-trace-stored">
              The original trace file is saved on this ticket and can be opened from Documents.
            </p>
          )}
          {!!(receipt?.users?.length || receipt?.transactions?.length) && (
            <p className="text-xs text-muted-foreground" data-testid="catalog-role-trace-metadata">
              {[
                receipt.users?.length ? `Users: ${receipt.users.join(', ')}` : '',
                receipt.transactions?.length ? `Transactions: ${receipt.transactions.join(', ')}` : '',
              ].filter(Boolean).join(' · ')}
            </p>
          )}
          {(selectionError || serverError || receipt?.status === 'failed' || receipt?.status === 'no_checks') && (
            <button type="button" className="text-xs underline" data-testid="catalog-role-trace-error-toggle" onClick={onToggleDetails}>
              {detailsOpen ? 'Hide details' : 'View details'}
            </button>
          )}
          {detailsOpen && (
            <p className="rounded-md bg-muted px-3 py-2 text-xs" data-testid="catalog-role-trace-error">
              {selectionError || serverError?.detail || receipt?.detail || serverError?.code}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function keyLinked(rows) {
  return rows.some((row) => String(row.field || '').toUpperCase() === 'FCLM_BUKRS' || String(row.low || '').toUpperCase() === '$BUKRS');
}

function groupAppliedOrg(rows) {
  const groups = new Map();
  rows.forEach((row) => {
    const key = orgKey(row.field);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(row.low);
  });
  return [...groups.entries()];
}

function groupOpenFields(rows) {
  const groups = new Map();
  rows.forEach((row) => {
    const key = `${row.object}|${row.auth}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(row);
  });
  return [...groups.entries()];
}
